import { supabase } from './supabaseClient';
import { createStudentFolder } from './storageService';

/**
 * Генерация уникального slug для студента
 */
function generateSlug(name) {
  const baseSlug = name.toLowerCase().replace(/\s/g, '-').replace(/[^a-z0-9-]/g, '');
  const timestamp = Date.now();
  return `${baseSlug}-${timestamp}`;
}

/**
 * Best-effort: if an authenticated user that localStorage says is a teacher
 * doesn't have a corresponding teachers row, create one. This rescues
 * sign-ups where EmailConfirmation either wasn't reached or fell back to
 * a wrong role guess, which used to leave the teacher stuck on
 * "Teacher not found in database" forever. Returns the teachers row on
 * success, or null if we couldn't heal it (caller should still throw).
 */
async function ensureTeacherRow(authUser) {
  if (!authUser?.id) return null;
  try {
    const stored = JSON.parse(localStorage.getItem('currentUser') || 'null');
    const pending = JSON.parse(localStorage.getItem('pendingRegistration') || 'null');
    const looksLikeTeacher =
      stored?.role === 'teacher' ||
      pending?.role === 'teacher' ||
      authUser.user_metadata?.role === 'teacher';
    if (!looksLikeTeacher) return null;

    const name = authUser.user_metadata?.name
      || stored?.name
      || pending?.name
      || authUser.email?.split('@')[0]
      || 'Teacher';
    // teachers.password_hash was dropped when auth moved to Supabase Auth;
    // including it now returns PGRST204 "column not found in schema cache".
    const { error: insertErr } = await supabase
      .from('teachers')
      .insert({
        auth_user_id: authUser.id,
        email: authUser.email,
        name,
        phone: null,
      });
    // 23505 = unique violation. Either way, re-fetch.
    if (insertErr && insertErr.code !== '23505') {
      console.warn('ensureTeacherRow insert error:', insertErr.message);
    }
    const { data, error } = await supabase
      .from('teachers')
      .select('*')
      .eq('auth_user_id', authUser.id)
      .maybeSingle();
    if (error) {
      console.warn('ensureTeacherRow lookup error:', error.message);
      return null;
    }
    return data || null;
  } catch (e) {
    console.warn('ensureTeacherRow exception:', e?.message);
    return null;
  }
}

/**
 * Получить текущего пользователя
 */
async function getCurrentUser() {
  // Получаем пользователя из Supabase Auth
  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (!authUser) {
    // Fallback: проверяем localStorage
    const currentUserStr = localStorage.getItem('currentUser');
    if (currentUserStr) {
      try {
        return JSON.parse(currentUserStr);
      } catch (e) {
        console.error('Error parsing currentUser from localStorage:', e);
      }
    }
    return null;
  }

  // Сначала пытаемся получить данные преподавателя из таблицы teachers
  let { data: teacherData, error: teacherError } = await supabase
    .from('teachers')
    .select('*')
    .eq('auth_user_id', authUser.id)
    .maybeSingle();

  // Self-heal: row missing for a known-teacher session — create it on the fly.
  if ((teacherError || !teacherData)) {
    const healed = await ensureTeacherRow(authUser);
    if (healed) teacherData = healed;
  }

  if (teacherData) {
    return {
      ...teacherData,
      id: teacherData.auth_user_id, // Используем auth_user_id как основной ID
      role: 'teacher'
    };
  }

  // Если не преподаватель, получаем из таблицы users (для студентов)
  const { data: userData, error: userError } = await supabase
    .from('users')
    .select('*')
    .eq('id', authUser.id)
    .single();

  if (!userError && userData) {
    return userData;
  }

  // Fallback: возвращаем из localStorage
  const currentUserStr = localStorage.getItem('currentUser');
  if (currentUserStr) {
    try {
      return JSON.parse(currentUserStr);
    } catch (e) {
      console.error('Error parsing currentUser from localStorage:', e);
    }
  }

  return null;
}

/**
 * Получить всех студентов текущего преподавателя
 */
export async function getStudents() {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    // Получаем teacher.id из таблицы teachers по auth_user_id
    const { data: teacher, error: teacherError } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', user.id)
      .maybeSingle();

    if (teacherError || !teacher) {
      console.error('Error fetching teacher:', teacherError);
      throw new Error('Teacher not found in database');
    }

    // Теперь используем teacher.id для фильтрации студентов
    const { data, error } = await supabase
      .from('students')
      .select('*')
      .eq('teacher_id', teacher.id) // Используем teacher.id из таблицы teachers
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Конвертируем в формат, совместимый с существующим кодом
    const studentsMap = {};
    data.forEach(student => {
      const slug = student.invite_slug || generateSlug(student.name);
      studentsMap[slug] = {
        ...student,
        slug: slug,
        studentId: student.id,
        studentSlug: slug
      };
    });

    return studentsMap;
  } catch (error) {
    console.error('Error fetching students:', error);
    throw error;
  }
}

/**
 * Создать нового студента
 */
export async function createStudent(studentData) {
  try {
    const user = await getCurrentUser();
    if (!user || user.role !== 'teacher') {
      throw new Error('User not authenticated or not a teacher');
    }

    // Получаем teacher.id из таблицы teachers по auth_user_id
    let { data: teacher, error: teacherError } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', user.id)
      .maybeSingle();

    // Self-heal: if the teachers row didn't land at sign-up (Supabase quirk
    // around RLS or email-confirmation timing), create it now using the
    // current auth session before failing the create.
    if (teacherError || !teacher) {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const healed = await ensureTeacherRow(authUser);
      if (healed) {
        teacher = { id: healed.id };
        teacherError = null;
      }
    }

    if (teacherError || !teacher) {
      console.error('Error fetching teacher:', teacherError);
      throw new Error('Teacher not found in database');
    }

    const slug = generateSlug(studentData.name);
    const inviteLink = `${window.location.origin}/register/${slug}`;

    console.log('Creating student with teacher_id:', teacher.id);

    const { data, error } = await supabase
      .from('students')
      .insert({
        teacher_id: teacher.id, // Используем teacher.id из таблицы teachers
        name: studentData.name,
        email: studentData.email || null,
        phone: studentData.phone || null,
        date_of_birth: studentData.dateOfBirth || null,
        proficiency: studentData.proficiency || null,
        price: studentData.price || null,
        price_currency: studentData.priceCurrency || null,
        status: studentData.status || 'active',
        invite_slug: slug,
        invite_link: inviteLink
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating student:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });
      throw error;
    }

    // Создаем папку для студента в Storage
    // Используем user.id (auth_user_id) для структуры папок, так как RLS политики проверяют auth.uid()
    console.log('📁 Creating student folder:', { 
      studentId: data.id, 
      teacherId: user.id,
      userObject: user,
      authUserId: user.id
    });
    
    // Проверяем текущего пользователя из auth
    const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      console.error('❌ Error getting auth user:', authError);
    }
    console.log('Current auth user:', { 
      id: authUser?.id, 
      email: authUser?.email 
    });
    
    // Используем authUser.id для соответствия RLS политикам
    const teacherAuthId = authUser?.id || user.id;
    console.log('📁 Will create folder with teacherId:', teacherAuthId);
    
    try {
      const folderCreated = await createStudentFolder(data.id, teacherAuthId);
      if (folderCreated) {
        console.log('✅ Student folder created successfully');
      } else {
        console.error('❌ Student folder creation returned false');
        console.error('This means the folder creation failed. Check logs above for details.');
      }
    } catch (folderError) {
      // Не выбрасываем ошибку, так как папка может быть создана позже
      console.error('❌ Could not create student folder immediately:', folderError);
      console.error('Folder error details:', {
        message: folderError.message,
        code: folderError.code,
        statusCode: folderError.statusCode,
        error: folderError.error,
        stack: folderError.stack
      });
    }

    // Возвращаем в формате, совместимом с существующим кодом
    return {
      ...data,
      slug: slug,
      studentId: data.id,
      studentSlug: slug
    };
  } catch (error) {
    console.error('Error creating student:', error);
    throw error;
  }
}

/**
 * Обновить студента
 */
export async function updateStudent(studentId, updates) {
  try {
    const user = await getCurrentUser();
    
    // Если обновляется user_account_id, это может делать сам студент при регистрации
    // В этом случае не требуем авторизации преподавателя
    const isUpdatingUserAccount = updates.user_account_id !== undefined;
    
    console.log('updateStudent called:', {
      studentId,
      isUpdatingUserAccount,
      user_account_id: updates.user_account_id,
      currentUser: user?.id,
      updates
    });
    
    if (!isUpdatingUserAccount && !user) {
      throw new Error('User not authenticated');
    }

    // Получаем teacher.id из таблицы teachers, если это преподаватель
    let teacherId = null;
    if (!isUpdatingUserAccount && user) {
      const { data: teacher, error: teacherError } = await supabase
        .from('teachers')
        .select('id')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      
      if (teacherError) {
        console.error('Error fetching teacher:', teacherError);
        throw new Error('Teacher not found in database');
      }
      
      if (teacher) {
        teacherId = teacher.id;
      } else {
        throw new Error('Teacher not found');
      }
    }

    // Подготавливаем данные для обновления
    const updateData = {
      updated_at: new Date().toISOString()
    };

    // Добавляем поля только если они переданы
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.email !== undefined) updateData.email = updates.email || null;
    if (updates.phone !== undefined) updateData.phone = updates.phone || null;
    if (updates.dateOfBirth !== undefined) updateData.date_of_birth = updates.dateOfBirth || null;
    if (updates.proficiency !== undefined) updateData.proficiency = updates.proficiency || null;
    if (updates.price !== undefined) updateData.price = updates.price || null;
    if (updates.priceCurrency !== undefined) updateData.price_currency = updates.priceCurrency || null;
    if (updates.status !== undefined) updateData.status = updates.status || 'active';
    if (updates.payments !== undefined) updateData.payments = updates.payments;
    
    // Если обновляется user_account_id при регистрации, используем RPC функцию
    // которая обходит RLS и гарантированно обновит данные
    if (isUpdatingUserAccount && updates.user_account_id) {
      console.log('updateStudent: Updating student registration data (user_account_id, email, name)');
      console.log('updateStudent: Using RPC function to bypass RLS');
      
      // Пробуем использовать RPC функцию для обновления
      const { data: rpcData, error: rpcError } = await supabase.rpc('update_student_on_registration', {
        p_student_id: studentId,
        p_user_account_id: updates.user_account_id,
        p_email: updates.email || null,
        p_name: updates.name || null
      });
      
      if (!rpcError && rpcData && rpcData.length > 0) {
        console.log('updateStudent: RPC function success!', rpcData[0]);
        return {
          ...rpcData[0],
          slug: rpcData[0].invite_slug,
          studentId: rpcData[0].id,
          studentSlug: rpcData[0].invite_slug
        };
      } else if (rpcError) {
        console.warn('updateStudent: RPC function failed, falling back to direct update:', rpcError);
        // Продолжаем с обычным обновлением
      }
      
      // Fallback: обычное обновление, если RPC функция не работает
      updateData.user_account_id = updates.user_account_id;
    } else if (updates.user_account_id !== undefined) {
      // Для обычных обновлений (не при регистрации)
      updateData.user_account_id = updates.user_account_id || null;
    }

    // Удаляем undefined поля
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    console.log('updateStudent: Prepared updateData:', updateData);

    let query = supabase
      .from('students')
      .update(updateData)
      .eq('id', studentId);

    // Если это не обновление user_account_id, проверяем teacher_id
    if (!isUpdatingUserAccount && teacherId) {
      query = query.eq('teacher_id', teacherId);
    }

    console.log('updateStudent: Executing query for studentId:', studentId);
    const { data, error } = await query.select();

    if (error) {
      console.error('updateStudent: Error from Supabase:', error);
      console.error('updateStudent: Error code:', error.code);
      console.error('updateStudent: Error message:', error.message);
      console.error('updateStudent: Error details:', error.details);
      throw error;
    }

    // Проверяем, что данные обновлены
    if (!data || data.length === 0) {
      throw new Error('Student not found or update failed');
    }

    const updatedStudent = data[0];
    console.log('updateStudent: Success! Updated student:', updatedStudent);

    // Проверяем права доступа только если это не обновление user_account_id
    if (!isUpdatingUserAccount && teacherId && updatedStudent && updatedStudent.teacher_id !== teacherId) {
      throw new Error('Unauthorized: Student does not belong to this teacher');
    }

    return {
      ...updatedStudent,
      slug: updatedStudent.invite_slug,
      studentId: updatedStudent.id,
      studentSlug: updatedStudent.invite_slug
    };
  } catch (error) {
    console.error('Error updating student:', error);
    throw error;
  }
}

/**
 * Удалить студента
 */
export async function deleteStudent(studentId) {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    // For teachers, resolve real teachers.id (students.teacher_id points to teachers.id)
    let teacherDbId = null;
    if (user.role === 'teacher') {
      const { data: teacher, error: teacherError } = await supabase
        .from('teachers')
        .select('id')
        .eq('auth_user_id', user.id)
        .maybeSingle();

      if (teacherError || !teacher) {
        console.error('Error fetching teacher:', teacherError);
        throw new Error('Teacher not found in database');
      }
      teacherDbId = teacher.id;
    }

    const { error } = await supabase
      .from('students')
      .delete()
      .eq('id', studentId)
      .eq('teacher_id', teacherDbId || user.id); // Ensure match against teachers.id

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting student:', error);
    throw error;
  }
}

/**
 * Получить студента по ID
 */
/**
 * Загрузить ТОЛЬКО payments студента напрямую из Supabase, в обход
 * localStorage-кэша студента (novas_students_data), который StudentProfile.js
 * использует для остальных полей и обновляет только при первом обращении к
 * несуществующему в кэше студенту — если студент уже закэширован, страница
 * никогда не перезапрашивает Supabase заново, и payments там могли остаться
 * от давнего визита, даже когда в Supabase уже актуальные данные.
 */
export async function getStudentPaymentsById(studentId) {
  try {
    const { data, error } = await supabase
      .from('students')
      .select('payments')
      .eq('id', studentId)
      .maybeSingle();
    if (error) throw error;
    return Array.isArray(data?.payments) ? data.payments : [];
  } catch (error) {
    console.error('Error in getStudentPaymentsById:', error);
    return null;
  }
}

export async function getStudentById(studentId) {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('students')
      .select('*')
      .eq('id', studentId)
      .eq('teacher_id', user.id)
      .single();

    if (error) throw error;

    // Pull the registered user's account (phone / email / name as the
    // student last set them) so the teacher's view shows the latest
    // values instead of the contact_phone snapshot.
    let userAccount = null;
    if (data.user_account_id) {
      const { data: ua, error: uaErr } = await supabase
        .from('users')
        .select('id, email, name, phone')
        .eq('id', data.user_account_id)
        .maybeSingle();
      if (!uaErr && ua) userAccount = ua;
    }

    return {
      ...data,
      slug: data.invite_slug,
      studentId: data.id,
      studentSlug: data.invite_slug,
      userAccount,
      isRegistered: !!data.user_account_id,
      contactPhone: data.contact_phone || '',
    };
  } catch (error) {
    console.error('Error fetching student:', error);
    throw error;
  }
}

/**
 * Получить студента по invite slug (публичный endpoint)
 * Включает имя преподавателя для отображения в приглашении
 */
export async function getStudentByInviteSlug(slug) {
  try {
    console.log('getStudentByInviteSlug called with slug:', slug);
    
    // Сначала получаем студента (используем maybeSingle для обработки случая, когда студент не найден)
    const { data: studentData, error: studentError } = await supabase
      .from('students')
      .select('*')
      .eq('invite_slug', slug)
      .maybeSingle();

    if (studentError) {
      console.error('getStudentByInviteSlug error:', studentError);
      console.error('Error code:', studentError.code);
      console.error('Error message:', studentError.message);
      console.error('Error details:', studentError.details);
      // Не выбрасываем ошибку, возвращаем null
      return null;
    }
    
    if (!studentData) {
      console.log('getStudentByInviteSlug: Student not found for slug:', slug);
      return null;
    }
    
    console.log('getStudentByInviteSlug success, student data:', studentData);

    // Затем получаем данные преподавателя
    let teacherData = {
      name: 'NOVAS',
      email: '',
      phone: '',
      photo: null
    };
    
    if (studentData.teacher_id) {
      try {
        console.log('getStudentByInviteSlug - fetching teacher with teacher_id:', studentData.teacher_id);
        const { data: teacher, error: teacherError } = await supabase
          .from('teachers')
          .select('name, email, phone, photo, timezone')
          .eq('id', studentData.teacher_id)
          .maybeSingle();

        console.log('getStudentByInviteSlug - teacher query result:', { teacher, teacherError });

        if (teacherError) {
          console.error('getStudentByInviteSlug - teacher query error:', {
            code: teacherError.code,
            message: teacherError.message,
            details: teacherError.details,
            hint: teacherError.hint
          });
          
          // Если ошибка связана с RLS, это нормально для неавторизованных пользователей
          if (teacherError.code === '42501' || teacherError.message?.includes('permission denied')) {
            console.warn('getStudentByInviteSlug - RLS policy blocked teacher access (user may not be authenticated)');
          }
        }

        if (!teacherError && teacher) {
          teacherData = {
            name: teacher.name || 'NOVAS',
            email: teacher.email || '',
            phone: teacher.phone || '',
            photo: teacher.photo || null,
            timezone: teacher.timezone || null
          };
          console.log('getStudentByInviteSlug - teacher data loaded:', teacherData);
        } else if (!teacher) {
          console.warn('getStudentByInviteSlug - teacher not found for teacher_id:', studentData.teacher_id);
        }
      } catch (teacherErr) {
        console.error('getStudentByInviteSlug - error fetching teacher:', teacherErr);
        // Продолжаем с дефолтными данными
      }
    } else {
      console.warn('getStudentByInviteSlug - studentData.teacher_id is missing:', studentData);
    }
    
    // Получаем email из Supabase Auth по user_account_id (если студент зарегистрирован)
    let studentEmail = studentData.email || '';
    if (studentData.user_account_id) {
      try {
        // Получаем email из auth.users через текущего пользователя
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser && authUser.id === studentData.user_account_id) {
          studentEmail = authUser.email || studentData.email || '';
          console.log('getStudentByInviteSlug - email from auth:', studentEmail);
        } else {
          // Если текущий пользователь не совпадает, используем email из таблицы students
          studentEmail = studentData.email || '';
          console.log('getStudentByInviteSlug - using email from students table:', studentEmail);
        }
      } catch (authErr) {
        console.warn('getStudentByInviteSlug - error getting email from auth:', authErr);
        studentEmail = studentData.email || '';
      }
    }
    
    // Подгружаем userAccount — это то, что обновляет сам студент в
    // своих profile-настройках (users.phone). Teacher's StudentProfile
    // отдаёт ему приоритет над students.contact_phone (последний — это
    // заметка учителя). Раньше эту секцию не подгружали, поэтому
    // обновлённый телефон студента не показывался у учителя.
    let userAccount = null;
    let isRegistered = false;
    if (studentData.user_account_id) {
      isRegistered = true;
      try {
        const { data: ua, error: uaErr } = await supabase
          .from('users')
          .select('id, email, name, phone')
          .eq('id', studentData.user_account_id)
          .maybeSingle();
        if (!uaErr && ua) userAccount = ua;
      } catch (uaExc) {
        console.warn('getStudentByInviteSlug - userAccount fetch failed:', uaExc?.message);
      }
    }

    // Убеждаемся, что teacher объект не содержит данных студента
    const cleanTeacherData = {
      name: teacherData.name,
      email: teacherData.email,
      phone: teacherData.phone,
      photo: teacherData.photo
    };

    console.log('getStudentByInviteSlug - returning student with teacher:', {
      studentName: studentData.name,
      studentEmail: studentEmail,
      teacherName: cleanTeacherData.name,
      teacherData: cleanTeacherData
    });
    
    return {
      ...studentData,
      slug: studentData.invite_slug,
      studentId: studentData.id,
      studentSlug: studentData.invite_slug,
      originalSlug: studentData.invite_slug,
      teacherName: cleanTeacherData.name,
      teacher: cleanTeacherData,
      email: studentEmail, // Добавляем email из Auth
      contactEmail: studentEmail, // Для совместимости
      userAccount,            // Phone / email / name updated by the student
      isRegistered,
      contactPhone: studentData.contact_phone || '',
    };
  } catch (error) {
    console.error('Error fetching student by invite slug:', error);
    return null;
  }
}

/**
 * Получить студента по user_account_id
 */
export async function getStudentByUserId(userId) {
  try {
    console.log('getStudentByUserId called with userId:', userId);
    
    const { data, error } = await supabase
      .from('students')
      .select('*')
      .eq('user_account_id', userId)
      .maybeSingle();

    if (error) {
      console.error('getStudentByUserId error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      // Возвращаем null вместо throw, чтобы не ломать логику
      return null;
    }
    
    console.log('getStudentByUserId success, student data:', data);
    
    // Если студент не найден, возвращаем null
    if (!data) {
      console.log('getStudentByUserId: Student not found (data is null)');
      return null;
    }
    
    // Получаем данные преподавателя
    let teacherData = {
      name: 'NOVAS',
      email: '',
      phone: '',
      photo: null
    };
    
    // Получаем email из Supabase Auth по user_account_id
    let studentEmail = data.email || '';
    if (data.user_account_id) {
      try {
        // Получаем email из auth.users через admin API или через текущего пользователя
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser && authUser.id === data.user_account_id) {
          studentEmail = authUser.email || data.email || '';
          console.log('getStudentByUserId - email from auth:', studentEmail);
        } else {
          // Если текущий пользователь не совпадает, используем email из таблицы students
          studentEmail = data.email || '';
          console.log('getStudentByUserId - using email from students table:', studentEmail);
        }
      } catch (authErr) {
        console.warn('getStudentByUserId - error getting email from auth:', authErr);
        studentEmail = data.email || '';
      }
    }
    
    // ВАЖНО: Проверяем, что data существует перед чтением teacher_id
    if (data && data.teacher_id) {
      try {
        console.log('getStudentByUserId - fetching teacher with teacher_id:', data.teacher_id);
        const { data: teacher, error: teacherError } = await supabase
          .from('teachers')
          .select('name, email, phone, photo, timezone')
          .eq('id', data.teacher_id)
          .maybeSingle();

        console.log('getStudentByUserId - teacher query result:', { teacher, teacherError });

        if (teacherError) {
          console.error('getStudentByUserId - teacher query error:', {
            code: teacherError.code,
            message: teacherError.message,
            details: teacherError.details,
            hint: teacherError.hint
          });
          
          // Если ошибка связана с RLS, это может означать проблему с политикой
          if (teacherError.code === '42501' || teacherError.message?.includes('permission denied')) {
            console.error('getStudentByUserId - RLS policy blocked teacher access. Check if RLS policy is applied.');
          }
        }

        if (!teacherError && teacher) {
          teacherData = {
            name: teacher.name || 'NOVAS',
            email: teacher.email || '',
            phone: teacher.phone || '',
            photo: teacher.photo || null,
            timezone: teacher.timezone || null
          };
          console.log('getStudentByUserId - teacher data loaded:', teacherData);
        } else if (!teacher) {
          console.warn('getStudentByUserId - teacher not found for teacher_id:', data.teacher_id);
        }
      } catch (teacherErr) {
        console.error('getStudentByUserId - error fetching teacher:', teacherErr);
      }
    } else {
      console.warn('getStudentByUserId - data.teacher_id is missing:', data);
    }
    
    return {
      ...data,
      slug: data.invite_slug,
      studentId: data.id,
      studentSlug: data.invite_slug,
      teacherName: teacherData.name,
      teacher: teacherData,
      email: studentEmail, // Добавляем email из Auth
      contactEmail: studentEmail // Для совместимости
    };
  } catch (error) {
    console.error('Error fetching student by user ID:', error);
    return null;
  }
}

